import React from "react";

import style from "./styles.module.scss";

require("@material/web/select/filled-select.js");
require("@material/web/select/outlined-select.js");
require("@material/web/select/select-option.js");

export type SelectNewVariant = "filled" | "outlined";
export type SelectNewValue = string | number;

export interface SelectNewOption {
  value: SelectNewValue;
  label: string;
  disabled?: boolean;
}

export interface MaterialSelectElement extends HTMLElement {
  value: string;
  selectedIndex: number;
  disabled: boolean;
  error: boolean;
  validity: ValidityState;
  validationMessage: string;
  checkValidity(): boolean;
  reportValidity(): boolean;
  reset(): void;
  select(value: string): void;
  selectIndex(index: number): void;
  showPicker(): void;
  updateComplete?: Promise<unknown>;
}

export type MaterialSelectEvent = React.ChangeEvent<
  MaterialSelectElement
>;

export type MaterialSelectEventHandler = (
  event: MaterialSelectEvent,
) => void;

interface SelectIconProps extends React.HTMLAttributes<HTMLElement> {
  "aria-label"?: string;
}

interface MaterialProperties {
  [key: string]: unknown;
}

export type SelectNewProps = Omit<
  React.HTMLAttributes<HTMLElement>,
  "defaultValue" | "onChange" | "onInput"
> & {
  options: ReadonlyArray<SelectNewOption>;
  variant?: SelectNewVariant;
  label?: string;
  name?: string;
  value?: SelectNewValue;
  defaultValue?: SelectNewValue;
  placeholder?: string;
  supportingText?: string;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  required?: boolean;
  noAsterisk?: boolean;
  quick?: boolean;
  menuPositioning?: "absolute" | "fixed" | "popover";
  clampMenuWidth?: boolean;
  leadingIcon?: React.ReactElement<SelectIconProps>;
  fullWidth?: boolean;
  onInput?: MaterialSelectEventHandler;
  onChange?: MaterialSelectEventHandler;
};

const variantElements: { [key in SelectNewVariant]: string } = {
  filled: "md-filled-select",
  outlined: "md-outlined-select",
};

function addBooleanAttribute(
  properties: MaterialProperties,
  name: string,
  enabled: boolean,
) {
  if (enabled) {
    properties[name] = "";
  }
}

function addDefinedProperty(
  properties: MaterialProperties,
  name: string,
  value: unknown,
) {
  if (value !== undefined) {
    properties[name] = value;
  }
}

function normalizeValue(value?: SelectNewValue): string | undefined {
  return value === undefined ? undefined : String(value);
}

const SelectNew = React.forwardRef<MaterialSelectElement, SelectNewProps>(
  (
    {
      options,
      variant = "filled",
      label,
      name,
      value,
      defaultValue,
      placeholder,
      supportingText,
      error = false,
      errorText,
      disabled = false,
      required = false,
      noAsterisk = false,
      quick = false,
      menuPositioning,
      clampMenuWidth = true,
      leadingIcon,
      fullWidth = false,
      onInput,
      onChange,
      className = "",
      ...htmlAttributes
    },
    ref,
  ) => {
    const selectRef = React.useRef<MaterialSelectElement | null>(null);

    const setSelectRef = (element: MaterialSelectElement | null) => {
      selectRef.current = element;

      if (typeof ref === "function") {
        ref(element);
      } else if (ref) {
        (ref as React.MutableRefObject<MaterialSelectElement | null>).current = element;
      }
    };

    React.useEffect(() => {
      const element = selectRef.current;

      if (!element) {
        return undefined;
      }

      const handleChange = (event: Event) => {
        if (onChange) {
          onChange(event as unknown as MaterialSelectEvent);
        }
      };

      const handleInput = (event: Event) => {
        if (onInput) {
          onInput(event as unknown as MaterialSelectEvent);
        }
      };

      element.addEventListener("change", handleChange);
      element.addEventListener("input", handleInput);

      return () => {
        element.removeEventListener("change", handleChange);
        element.removeEventListener("input", handleInput);
      };
    }, [onChange, onInput]);

    React.useEffect(() => {
      const element = selectRef.current;

      if (!element) {
        return undefined;
      }

      const patched = new WeakSet<Element>();

      const patch = () => {
        element.querySelectorAll("md-select-option").forEach((option) => {
          const root = option.shadowRoot;

          if (!root || patched.has(option)) {
            return;
          }

          const styleElement = document.createElement("style");
          styleElement.textContent =
            ".list-item.selected slot[name=\"headline\"]::slotted(*) { color: var(--md-sys-color-on-secondary-container) !important; }";
          root.appendChild(styleElement);
          patched.add(option);
        });
      };

      patch();
      element.addEventListener("opening", patch);

      return () => element.removeEventListener("opening", patch);
    }, [options]);

    const classes = [
      style.select,
      fullWidth ? style.selectFullWidth : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const selectedValue = normalizeValue(
      value !== undefined ? value : defaultValue,
    );

    const optionsKey = options.map((option) => String(option.value)).join("|");

    React.useEffect(() => {
      const element = selectRef.current;

      if (!element || selectedValue === undefined) {
        return undefined;
      }

      let active = true;

      const sync = () => {
        if (active) {
          element.select(selectedValue);
        }
      };

      if (element.updateComplete) {
        element.updateComplete.then(sync);
      } else {
        sync();
      }

      return () => {
        active = false;
      };
    }, [selectedValue, optionsKey]);

    const materialProperties: MaterialProperties = {
      ...htmlAttributes,
      ref: setSelectRef,
      class: classes,
    };

    addDefinedProperty(materialProperties, "label", label);
    addDefinedProperty(materialProperties, "name", name);
    addDefinedProperty(materialProperties, "value", selectedValue);
    addDefinedProperty(
      materialProperties,
      "supporting-text",
      supportingText,
    );
    addDefinedProperty(materialProperties, "error-text", errorText);
    addDefinedProperty(
      materialProperties,
      "menu-positioning",
      menuPositioning,
    );

    addBooleanAttribute(materialProperties, "error", error);
    addBooleanAttribute(materialProperties, "disabled", disabled);
    addBooleanAttribute(materialProperties, "required", required);
    addBooleanAttribute(materialProperties, "no-asterisk", noAsterisk);
    addBooleanAttribute(materialProperties, "quick", quick);
    addBooleanAttribute(
      materialProperties,
      "clamp-menu-width",
      clampMenuWidth,
    );
    addBooleanAttribute(
      materialProperties,
      "has-leading-icon",
      Boolean(leadingIcon),
    );

    const slottedLeadingIcon = leadingIcon
      ? React.cloneElement(leadingIcon, {
          "key": "leading-icon",
          "slot": "leading-icon",
          "className": [style.selectIcon, leadingIcon.props.className]
            .filter(Boolean)
            .join(" "),
          "aria-hidden": leadingIcon.props["aria-label"] ? undefined : true,
        })
      : null;

    const optionElements: React.ReactNode[] = [];

    if (placeholder) {
      const placeholderProperties: MaterialProperties = {
        key: "placeholder",
        value: "",
        class: style.option,
      };

      addBooleanAttribute(
        placeholderProperties,
        "selected",
        selectedValue === undefined || selectedValue === "",
      );
      addBooleanAttribute(placeholderProperties, "disabled", true);

      optionElements.push(
        React.createElement(
          "md-select-option",
          placeholderProperties,
          React.createElement(
            "span",
            { slot: "headline", className: style.optionText },
            placeholder,
          ),
        ),
      );
    }

    options.forEach((option) => {
      const optionValue = String(option.value);
      const optionProperties: MaterialProperties = {
        key: optionValue,
        value: optionValue,
        class: style.option,
      };

      addBooleanAttribute(
        optionProperties,
        "selected",
        selectedValue === optionValue,
      );
      addBooleanAttribute(
        optionProperties,
        "disabled",
        Boolean(option.disabled),
      );

      optionElements.push(
        React.createElement(
          "md-select-option",
          optionProperties,
          React.createElement(
            "span",
            { slot: "headline", className: style.optionText },
            option.label,
          ),
        ),
      );
    });

    return React.createElement(
      variantElements[variant],
      materialProperties,
      slottedLeadingIcon,
      ...optionElements,
    );
  },
);

SelectNew.displayName = "SelectNew";

export default SelectNew;
